import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { LogoMark } from "./LogoMark";
export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    pathname,
    hash
  } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const reduce = useReducedMotion();
  const navRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Close the menu on navigation
  useEffect(() => setMenuOpen(false), [pathname, hash]);

  // Close on Escape (returning focus to the toggle) or on a click outside
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
    };
  }, [menuOpen]);

  // Once the page scrolls, the sticky bar gets a cream backing so it reads over content
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [{
    label: "Home",
    to: "/",
    active: pathname === "/" && !["#projects", "#thoughts", "#receipts"].includes(hash)
  }, {
    label: "Projects",
    to: "/#projects",
    active: pathname === "/" && hash === "#projects"
  }, {
    label: "Thoughts",
    to: "/#thoughts",
    active: pathname === "/" && hash === "#thoughts" || pathname.startsWith("/thoughts")
  }, {
    label: "Receipts",
    to: "/#receipts",
    active: pathname === "/" && hash === "#receipts"
  }, {
    label: "Contact",
    to: `${pathname}#contact`,
    active: false
  }];
  return <header className={`fixed inset-x-0 top-0 z-30 transition-[background-color,box-shadow] duration-200 ease-out ${scrolled ? "bg-cream/90 shadow-[0_1px_0_rgba(29,24,20,0.08)] backdrop-blur-md" : "bg-transparent"}`} data-id="element-157" data-mp="1hojnqqwix0lj">
      <div className={`flex items-center justify-between px-6 transition-[padding] duration-200 ease-out md:px-12 lg:px-20 ${scrolled ? "py-3 md:py-4" : "pb-3 pt-6 md:pb-4 md:pt-10"}`} data-id="element-158" data-mp="18ls96i8cpopo">
        <Link to="/" aria-label="Alyssa Retza, home" className="flex items-center gap-3 text-dusty" data-id="element-159" data-mp="13nz45sisb0qz">
          <LogoMark className="h-9 w-9 shrink-0 md:h-11 md:w-11" data-id="element-160" data-mp="2t3yirp569ie0" />
          <span className="hidden whitespace-nowrap text-2xl font-medium tracking-[-0.02em] sm:inline md:text-4xl" data-id="element-161" data-mp="2y1ro6fyni8d8">
            Alyssa Retza
          </span>
        </Link>

        <nav ref={navRef} className="relative flex items-center gap-2 md:gap-4" aria-label="Primary" data-id="element-162" data-mp="mck5ixbeuyqf">
          <Link to={`${pathname}#contact`} className="hidden h-12 items-center gap-3 whitespace-nowrap rounded-full bg-espresso px-5 min-[400px]:flex text-sm font-medium uppercase text-cream ring-1 ring-cream/10 transition-colors duration-150 ease-out hover:bg-bark md:h-14 md:px-8 md:text-base" data-id="element-163" data-mp="hdh0hcvfksig">
            Let’s talk
            <span className="h-1.5 w-1.5 rounded-full bg-mustard" aria-hidden="true" data-id="element-164" data-mp="1051f1ayqb0cv" />
          </Link>

          <button ref={toggleRef} type="button" onClick={() => setMenuOpen(o => !o)} aria-expanded={menuOpen} aria-controls="site-menu" className="flex h-12 items-center gap-3 whitespace-nowrap rounded-full bg-cream px-5 text-sm font-medium uppercase text-espresso ring-1 ring-espresso/10 transition-transform duration-150 ease-out active:scale-95 md:h-14 md:px-8 md:text-base" data-id="element-165" data-mp="1f20svgk6bpfs">
            {menuOpen ? "Close" : "Menu"}
            <span className="flex gap-1" aria-hidden="true" data-id="element-166" data-mp="27wgvgq60u9ju">
              <span className="h-1.5 w-1.5 rounded-full bg-dusty" data-id="element-167" data-mp="2cwbjmlyewl17" />
              <span className="h-1.5 w-1.5 rounded-full bg-dusty" data-id="element-168" data-mp="2cv0fum4yvda3" />
            </span>
          </button>

          <AnimatePresence data-id="element-169" data-mp="2gw3uj5ba339q">
            {menuOpen && <motion.div id="site-menu" initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -8,
            scale: 0.96
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -8,
            scale: 0.96
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="absolute right-0 top-full mt-3 w-64 origin-top-right rounded-3xl bg-cream p-3 text-espresso shadow-xl ring-1 ring-espresso/10" data-id="element-170" data-mp="1k9gz5p1lhlnq">
                <ul data-id="element-171" data-mp="15h6qao6iw7hs">
                  {links.map(link => <li key={link.label} data-id="element-172" data-mp="xyh1ykiyrdaz">
                      <Link to={link.to} onClick={() => {
                  setMenuOpen(false);
                  // Re-scroll even when the URL already has this hash
                  const [path, anchor] = link.to.split("#");
                  if (anchor && (path || "/") === pathname) {
                    document.getElementById(anchor)?.scrollIntoView({
                      behavior: "smooth"
                    });
                  } else if (!anchor && link.to === pathname) {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth"
                    });
                  }
                }} aria-current={link.active ? "page" : undefined} className="flex items-center justify-between rounded-2xl px-4 py-3 text-2xl font-medium transition-colors duration-150 hover:bg-paper" data-id="element-173" data-mp="xxeaobhqb8uj">
                        {link.label}
                        {link.active && <span className="h-2 w-2 rounded-full bg-rust" aria-hidden="true" data-id="element-174" data-mp="3is6shy2vm115" />}
                      </Link>
                    </li>)}
                </ul>
              </motion.div>}
          </AnimatePresence>
        </nav>
      </div>
    </header>;
}